import {
  authKey_local_storage_key,
  userId_local_storage_key,
  userInfo_local_storage_key,
  redirect_url_key,
} from "./common";

let logoutTimerId = null;

// 🎯 Clears every localStorage key this app uses to represent a logged-in
// session. There are two separate "user info" keys in this codebase —
// "webvi_userInfo" (written directly on sign-in, holds expiresAt) and
// userInfo_local_storage_key / "jobseekerspage_userInfo" (written by the
// Redux reducer) — both must be cleared, or the user still appears logged
// in wherever the other key is checked.
const clearAllAuthStorage = () => {
  localStorage.removeItem(authKey_local_storage_key);
  localStorage.removeItem(userId_local_storage_key);
  localStorage.removeItem(userInfo_local_storage_key);
  localStorage.removeItem("webvi_userInfo");
  localStorage.removeItem(redirect_url_key);
};

// 🎯 Checks the CURRENTLY stored expiresAt against right now, without
// scheduling anything. Used to guard against re-restoring an already
// expired session.
export const isStoredSessionExpired = () => {
  const rawUserInfo = localStorage.getItem("webvi_userInfo");
  const authKey = localStorage.getItem(authKey_local_storage_key);
  
  // If there is no session data at all, the user is just a guest (not expired)
  if (!rawUserInfo && !authKey) return false;
  if (!rawUserInfo) return true;

  try {
    const parsed = JSON.parse(rawUserInfo);
    const expiresAt = parsed?.data?.expiresAt;
    if (!expiresAt) return false; // unknown expiry — don't block, timer just won't schedule

    return new Date(expiresAt).getTime() - Date.now() <= 0;
  } catch (err) {
    return true;
  }
};

// 🎯 Runs the moment the token actually expires (or is found already
// expired on load) — clears the session and sends the user home with a
// full page reload, same as most real websites do on session expiry.
export const performAutoLogout = () => {
  // Check if there was actually an active session before logging out
  const hadSession = localStorage.getItem(authKey_local_storage_key) || localStorage.getItem("webvi_userInfo");

  clearScheduledLogout();
  clearAllAuthStorage();
  localStorage.setItem("session_expired_flag", "true");

  // Only redirect if there was an active session to expire, 
  // preventing guest users from being bounced when refreshing inner pages.
  if (hadSession) {
    const alreadyLoggedOut = !localStorage.getItem(authKey_local_storage_key);
    if (window.location.pathname !== "/" || !alreadyLoggedOut) {
      window.location.href = "/";
    }
  }
};

export const clearScheduledLogout = () => {
  if (logoutTimerId) {
    clearTimeout(logoutTimerId);
    logoutTimerId = null;
  }
};

// 🎯 Reads the stored token's expiresAt and sets a timer for the exact
// remaining time. Call this once on app load, and again right after login,
// so the countdown always reflects the real expiry — whether the user just
// logged in or is resuming a session from an earlier browser visit.
export const scheduleSessionExpiry = () => {
  clearScheduledLogout();

  const rawUserInfo = localStorage.getItem("webvi_userInfo");
  if (!rawUserInfo) return;

  try {
    const parsed = JSON.parse(rawUserInfo);
    const expiresAt = parsed?.data?.expiresAt;
    if (!expiresAt) return;

    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();

    if (msUntilExpiry <= 0) {
      performAutoLogout();
      return;
    }

    logoutTimerId = setTimeout(() => {
      performAutoLogout();
    }, msUntilExpiry);
  } catch (err) {
    console.error("Failed to schedule session expiry:", err);
  }
};